import AXIOS from 'axios';

// Standalone logger to avoid circular dependency
const LOG_ENDPOINT = 'https://zoiteckh.com/api/public/projector-error/';
const logUploadError = (payload) => {
  try {
    AXIOS.post(LOG_ENDPOINT, {
      website: 6,
      user_name: payload?.userName || 'Guest',
      function_affected: payload?.functionAffected || 'Upload Auth',
      description: payload?.description || 'Unknown error',
      severity: payload?.severity || 'high',
      browser_info: navigator?.userAgent || '',
      os_info: navigator?.platform || '',
      screen_resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      page_url: window?.location?.href || '',
      raw_data: {
        error_message: payload?.errorMessage || null,
        error_stack: payload?.errorStack || null,
        response_status: payload?.responseStatus || null,
        response_body: payload?.responseBody || null,
        request_url: payload?.requestUrl || null,
        request_method: payload?.requestMethod || null,
        timestamp: new Date().toISOString(),
        component: payload?.component || null,
        user_action: payload?.userAction || null,
      },
    }).catch(() => {}); // Fire and forget
  } catch (e) {
    // Silently ignore any logging errors - never break upload flow
  }
};

/**
 * Upload Service API Client
 *
 * This module provides axios configuration for the secure upload service
 * (uploader.projector.app) that uses presigned URLs instead of exposing
 * AWS credentials in the client.
 */

// Lazy import to avoid circular dependency
// getUploaderToken is imported at request time, not module load time
let getUploaderTokenFn = null;
const getUploaderToken = async () => {
  if (!getUploaderTokenFn) {
    // Dynamic import to break circular dependency
    const tokenManager = await import('./tokenManager');
    getUploaderTokenFn = tokenManager.getUploaderToken;
  }
  return getUploaderTokenFn();
};

// Environment-based URL selection
const PRODUCTION_UPLOADER = 'https://uploader.projector.app/api';
const PREPROD_UPLOADER = 'https://uploader.preprod.projector.app/api';

// Always use production upload service for all environments (matches mobile app)
const uploaderBaseUrl = PRODUCTION_UPLOADER;

/*
// Commented out - Environment-specific routing (can be restored if needed)
const getUploaderBaseUrl = () => {
  if (window.location.origin.includes('localhost')) {
    return PRODUCTION_UPLOADER;
  }
  if (window.location.origin.includes('preprod')) {
    return PREPROD_UPLOADER;
  }
  if (window.location.origin.includes('dev.app')) {
    return PREPROD_UPLOADER;
  }
  return PRODUCTION_UPLOADER;
};
const uploaderBaseUrl = getUploaderBaseUrl();
*/

console.log('[UploaderAPI] Using base URL:', uploaderBaseUrl);

// Create axios instance for upload service
const uploaderAxios = AXIOS.create({
  baseURL: uploaderBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to inject uploader JWT token
uploaderAxios.interceptors.request.use(
  async (req) => {
    try {
      // Get valid uploader token (JWT format)
      const token = await getUploaderToken();

      // Add token to Authorization header (Bearer token)
      if (token) {
        req.headers.Authorization = `Bearer ${token}`;
        console.log('[UploaderAPI] Token attached to request');
      } else {
        console.warn('[UploaderAPI] No uploader token available');
      }

      return req;
    } catch (error) {
      console.error('[UploaderAPI] Failed to get uploader token:', error);
      // Continue without token - backend will reject if auth is required
      return req;
    }
  },
  (error) => {
    console.error('[UploaderAPI] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor — retry once on 401 with a fresh token
uploaderAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const userName = `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest';

    if (error.response) {
      const originalRequest = error.config;

      // On 401: clear cached token, fetch a new one, and retry once
      if (error.response.status === 401 && !originalRequest.retried) {
        // eslint-disable-next-line no-param-reassign
        originalRequest.retried = true;
        console.warn('[UploaderAPI] 401 received, clearing token and retrying...');

        // Log the initial 401 (before retry) with full token details
        const currentUploaderToken = localStorage.getItem('uploader_token');
        const userToken = localStorage.getItem('token');
        logUploadError({
          userName,
          functionAffected: 'Upload Auth',
          description: `Upload API 401 — initial request failed, attempting token refresh. URL: ${originalRequest.url}`,
          severity: 'medium',
          errorMessage: 'Initial 401 - attempting retry',
          responseStatus: 401,
          responseBody: JSON.stringify({
            serverResponse: error.response.data,
            tokenUsed: currentUploaderToken ? `${currentUploaderToken.substring(0, 30)}...` : null,
            tokenLength: currentUploaderToken?.length || 0,
            hasUserToken: !!userToken,
            userTokenLength: userToken?.length || 0,
            authHeader: originalRequest.headers?.Authorization ? `${originalRequest.headers.Authorization.substring(0, 40)}...` : null,
          }),
          requestUrl: originalRequest.url,
          requestMethod: originalRequest.method,
          component: 'uploaderApi',
          userAction: 'upload request - 401 retry triggered',
        });

        try {
          localStorage.removeItem('uploader_token');
          localStorage.removeItem('uploader_token_expiry');

          const newToken = await getUploaderToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return uploaderAxios(originalRequest);
          }
        } catch (retryError) {
          console.error('[UploaderAPI] Token refresh failed:', retryError);

          // Log the retry failure - THIS IS THE KEY LOG
          const userTokenAfterRetry = localStorage.getItem('token');
          const uploaderTokenAfterRetry = localStorage.getItem('uploader_token');
          logUploadError({
            userName,
            functionAffected: 'Upload Auth',
            description: `Upload API 401 — token refresh FAILED: ${retryError.message}`,
            severity: 'high',
            errorMessage: retryError.message,
            errorStack: retryError.stack,
            responseBody: JSON.stringify({
              retryErrorMessage: retryError.message,
              hasUserToken: !!userTokenAfterRetry,
              userTokenLength: userTokenAfterRetry?.length || 0,
              userToken: userTokenAfterRetry ? `${userTokenAfterRetry.substring(0, 30)}...` : null,
              hasUploaderToken: !!uploaderTokenAfterRetry,
              uploaderTokenLength: uploaderTokenAfterRetry?.length || 0,
              localStorageKeys: Object.keys(localStorage),
            }),
            requestUrl: originalRequest.url,
            requestMethod: originalRequest.method,
            component: 'uploaderApi',
            userAction: 'upload request - 401 retry failed',
          });
        }
      }

      // Server responded with error status
      console.error('[UploaderAPI] Response error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error('[UploaderAPI] Network error: No response received');
    } else {
      // Error in request setup
      console.error('[UploaderAPI] Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Axios instance for gift uploads (no auth token - uses gift_uuid instead)
const giftUploaderAxios = AXIOS.create({
  baseURL: uploaderBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Response interceptor for error handling (same as uploaderAxios)
giftUploaderAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('[GiftUploaderAPI] Response error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    } else if (error.request) {
      console.error('[GiftUploaderAPI] Network error: No response received');
    } else {
      console.error('[GiftUploaderAPI] Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

export { uploaderAxios, uploaderBaseUrl, giftUploaderAxios };
